import type { Ordering } from "@prismicio/client"
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActionButton,
  DialogActionLink,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  FormField,
  RadioGroup,
  RadioItem,
} from "@prismicio/editor-ui"
import { useState } from "react"

import { useAPIExplorer } from "../../../APIExplorerContext"
import { RequestAPIPathFieldset } from "../RequestAPIPathFieldset"
import { allowedOrderingPaths } from "./constants"

interface OrderingConfigDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  editIndex?: number
}

export function OrderingConfigDialog(props: OrderingConfigDialogProps) {
  const { open, onOpenChange, editIndex } = props

  const { request } = useAPIExplorer()

  const isEdit = editIndex !== undefined

  const ordering =
    editIndex !== undefined ? request.orderings[editIndex] : undefined

  const [field, setField] = useState(ordering?.field || "")
  const [direction, setDirection] = useState<"asc" | "desc">(
    ordering?.direction || "asc",
  )

  function handleSubmit() {
    const newOrdering: Ordering = { field, direction }

    if (isEdit) {
      // Update existing ordering
      request.setOrderings([
        ...request.orderings.slice(0, editIndex),
        newOrdering,
        ...request.orderings.slice(editIndex + 1),
      ])
    } else {
      // Add new ordering
      request.setOrderings([...request.orderings, newOrdering])
    }
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="small">
      <DialogHeader title={`${isEdit ? "Edit" : "Add"} ordering`} />
      <DialogContent>
        <DialogDescription asChild>
          <Box
            padding={{ inline: 24, block: 16 }}
            display="grid"
            gridTemplateColumns="1fr 180px"
            alignItems="flex-start"
            gap={16}
          >
            <RequestAPIPathFieldset
              path={field}
              setPath={setField}
              description="A path to a field or document metadata property."
              allowedPaths={allowedOrderingPaths}
              context="ordering"
            />
            <FormField
              label="Direction"
              description={`Sort by ${direction === "asc" ? "ascending" : "descending"} order.`}
            >
              <RadioGroup value={direction} onChange={setDirection} asChild>
                <ButtonGroup density="compact">
                  <RadioItem value="asc" asChild>
                    <Button
                      startIcon="arrowUpward"
                      color={direction === "asc" ? "purple" : "grey"}
                      size="large"
                    />
                  </RadioItem>
                  <RadioItem value="desc" asChild>
                    <Button
                      startIcon="arrowDownward"
                      color={direction === "desc" ? "purple" : "grey"}
                      size="large"
                    />
                  </RadioItem>
                </ButtonGroup>
              </RadioGroup>
            </FormField>
          </Box>
        </DialogDescription>
      </DialogContent>
      <DialogActions>
        <DialogActionLink
          color="purple11"
          href="https://prismic.io/docs/rest-api-technical-reference#orderings"
          underline
        >
          Orderings documentation
        </DialogActionLink>
        <DialogCancelButton onClick={() => onOpenChange(false)} />
        <DialogActionButton onClick={handleSubmit} disabled={!field}>
          {isEdit ? "Update" : "Add"} ordering
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}
